import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Save, ChevronRight, Trash2, Pencil } from 'lucide-react';
import {
  AcademicYearsApi,
  classLabel,
  Exam,
  ExamsApi,
  ExamScheduleStatus,
  ExamStatus,
  ExamTimetableEntry,
  MissingMarksEntry,
  StudentsApi,
} from '@/services/school.api';
import { AcademicClassesApi, ClassSubjectRow } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

// Two distinct concepts, never conflated: PUBLISH_TONE is the stored
// draft/published flag; SCHEDULE_TONE is the Upcoming/Ongoing/Completed
// gate computed live from this exam's own timetable dates (legacy
// Exam.php:89-135) — no manual override, so there's no status picker here.
const PUBLISH_TONE: Record<ExamStatus, 'slate' | 'purple'> = {
  draft: 'slate',
  published: 'purple',
};

const SCHEDULE_TONE: Record<ExamScheduleStatus, 'blue' | 'amber' | 'green'> = {
  upcoming: 'blue',
  ongoing: 'amber',
  completed: 'green',
};

/** One entry per distinct subject taught to the class — dedupes ClassSubjectRow's per-section fan-out (same pattern as ClassSubjectsPage.tsx). */
function dedupeBySubject(rows: ClassSubjectRow[]): ClassSubjectRow[] {
  const seen = new Map<string, ClassSubjectRow>();
  for (const r of rows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, r);
  return [...seen.values()];
}

export function ExamsPage() {
  const [openExamId, setOpenExamId] = useState<string | null>(null);
  if (openExamId) {
    return <ExamWorkspace examId={openExamId} onBack={() => setOpenExamId(null)} />;
  }
  return <ExamsList onOpen={setOpenExamId} />;
}

// ───── List + create ──────────────────────────────────────────────────────────
function ExamsList({ onOpen }: { onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('/exams', 'create');
  const canDelete = can('/exams', 'delete');
  const [yearId, setYearId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [deletingExam, setDeletingExam] = useState<Exam | null>(null);

  const { data: years = [] } = useQuery({
    queryKey: ['academic-years'],
    queryFn: AcademicYearsApi.list,
  });
  const effectiveYearId = useMemo(
    () => yearId || years.find((y) => y.isCurrent)?.id || years[0]?.id || '',
    [yearId, years],
  );

  const { data: exams = [], isLoading } = useQuery({
    queryKey: ['exams', effectiveYearId],
    queryFn: () => ExamsApi.list({ academicYearId: effectiveYearId || undefined }),
    enabled: !!effectiveYearId,
  });

  const remove = useMutation({
    mutationFn: (id: string) => ExamsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exams'] });
      setDeletingExam(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Exams"
        description="Create exams, build their timetable, and enter subject-wise marks. Published results flow to Report Cards and the student & parent portal."
        actions={
          canWrite && (
            <button className="btn-primary" onClick={() => setCreating(true)} disabled={!effectiveYearId}>
              <Plus className="mr-1.5 h-4 w-4" /> New exam
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : exams.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          No exams yet. {canWrite && (
            <>
              Click <span className="font-medium">New exam</span> to create one.
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Exam</Th>
                <Th>Dates</Th>
                <Th>Status</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exams.map((ex) => (
                <tr key={ex.id} onClick={() => onOpen(ex.id)} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{ex.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {ex.startDate?.slice(0, 10)} → {ex.endDate?.slice(0, 10)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={SCHEDULE_TONE[ex.scheduleStatus]}>{ex.scheduleStatus}</Badge>
                      {ex.status === 'published' && <Badge tone={PUBLISH_TONE.published}>published</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canWrite && (
                        <button
                          className="text-slate-400 hover:text-brand-600"
                          title="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingExam(ex);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="text-slate-400 hover:text-red-600"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingExam(ex);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRight className="inline h-4 w-4 text-slate-400" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateExamModal
          academicYearId={effectiveYearId}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}

      {editingExam && (
        <EditExamModal
          exam={editingExam}
          onClose={() => setEditingExam(null)}
          onUpdated={() => {
            setEditingExam(null);
            qc.invalidateQueries({ queryKey: ['exams'] });
          }}
        />
      )}

      <ConfirmDialog
        open={!!deletingExam}
        onClose={() => setDeletingExam(null)}
        onConfirm={() => deletingExam && remove.mutate(deletingExam.id)}
        loading={remove.isPending}
        title="Delete exam?"
        message={
          (remove.error && errMsg(remove.error)) ??
          `"${deletingExam?.name}" and its timetable will be permanently removed. This can't be undone.`
        }
        confirmText="Delete"
      />
    </>
  );
}

const createSchema = z.object({
  name: z.string().min(1, 'Required'),
  startDate: z.string().min(1, 'Required'),
  endDate: z.string().min(1, 'Required'),
});
type CreateForm = z.infer<typeof createSchema>;

function CreateExamModal({
  academicYearId,
  onClose,
  onCreated,
}: {
  academicYearId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateForm>({ resolver: zodResolver(createSchema) });

  const create = useMutation({
    mutationFn: (f: CreateForm) =>
      ExamsApi.create({ name: f.name, academicYearId, startDate: f.startDate, endDate: f.endDate }),
    onSuccess: (exam) => {
      qc.invalidateQueries({ queryKey: ['exams'] });
      onCreated(exam.id);
    },
  });

  return (
    <Modal open onClose={onClose} title="New exam" size="md">
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Exam name</label>
          <Input placeholder="e.g. Mid-Term 1" {...register('name')} />
          {errors.name && <FieldErr msg={errors.name.message} />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Start date</label>
            <Input type="date" {...register('startDate')} />
            {errors.startDate && <FieldErr msg={errors.startDate.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">End date</label>
            <Input type="date" {...register('endDate')} />
            {errors.endDate && <FieldErr msg={errors.endDate.message} />}
          </div>
        </div>
        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create & build timetable'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditExamModal({
  exam,
  onClose,
  onUpdated,
}: {
  exam: Exam;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: exam.name,
      startDate: exam.startDate?.slice(0, 10),
      endDate: exam.endDate?.slice(0, 10),
    },
  });

  const update = useMutation({
    mutationFn: (f: CreateForm) => ExamsApi.update(exam.id, f),
    onSuccess: onUpdated,
  });

  return (
    <Modal open onClose={onClose} title="Edit exam" size="md">
      <form onSubmit={handleSubmit((f) => update.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Exam name</label>
          <Input placeholder="e.g. Mid-Term 1" {...register('name')} />
          {errors.name && <FieldErr msg={errors.name.message} />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Start date</label>
            <Input type="date" {...register('startDate')} />
            {errors.startDate && <FieldErr msg={errors.startDate.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">End date</label>
            <Input type="date" {...register('endDate')} />
            {errors.endDate && <FieldErr msg={errors.endDate.message} />}
          </div>
        </div>
        {update.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(update.error)}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ───── Workspace (per exam) ────────────────────────────────────────────────────
type Tab = 'timetable' | 'marks' | 'results';

function ExamWorkspace({ examId, onBack }: { examId: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('/exams', 'create');
  const canDelete = can('/exams', 'delete');
  const [tab, setTab] = useState<Tab>('timetable');

  const { data: exam } = useQuery({ queryKey: ['exam', examId], queryFn: () => ExamsApi.get(examId) });

  const [missing, setMissing] = useState<MissingMarksEntry[] | null>(null);
  const publish = useMutation({
    mutationFn: (override?: boolean) => ExamsApi.publish(examId, override),
    onSuccess: () => {
      setMissing(null);
      qc.invalidateQueries({ queryKey: ['exam', examId] });
      qc.invalidateQueries({ queryKey: ['exams'] });
      qc.invalidateQueries({ queryKey: ['exam-results', examId] });
    },
    onError: (e) => setMissing(missingFromError(e)),
  });

  const unpublish = useMutation({
    mutationFn: () => ExamsApi.unpublish(examId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exam', examId] });
      qc.invalidateQueries({ queryKey: ['exams'] });
    },
  });

  return (
    <>
      <PageHeader
        title={exam?.name ?? 'Exam'}
        description={
          exam ? (
            <span className="inline-flex items-center gap-2">
              <Badge tone={SCHEDULE_TONE[exam.scheduleStatus]}>{exam.scheduleStatus}</Badge>
              {exam.status === 'published' && <Badge tone={PUBLISH_TONE.published}>published</Badge>}
              {exam.startDate?.slice(0, 10)} → {exam.endDate?.slice(0, 10)}
            </span>
          ) : (
            ' '
          )
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary" onClick={onBack}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </button>
            {exam && canWrite && exam.status === 'draft' && exam.scheduleStatus === 'completed' && (
              <button className="btn-primary" onClick={() => publish.mutate(undefined)} disabled={publish.isPending}>
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </button>
            )}
            {exam && canDelete && exam.status === 'published' && (
              <button className="btn-secondary" onClick={() => unpublish.mutate()} disabled={unpublish.isPending}>
                {unpublish.isPending ? 'Unpublishing…' : 'Unpublish'}
              </button>
            )}
          </div>
        }
      />

      {missing && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="mb-2 font-medium">{missing.length} student/subject entries have no marks recorded:</div>
          <ul className="mb-3 max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5">
            {missing.map((m, i) => (
              <li key={i}>
                {m.studentName} — {m.subjectName} ({m.className})
              </li>
            ))}
          </ul>
          <button
            className="btn-secondary !bg-white"
            onClick={() => publish.mutate(true)}
            disabled={publish.isPending}
          >
            Publish anyway (missing entries count as fail)
          </button>
        </div>
      )}
      {publish.error && !missing && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(publish.error)}</div>
      )}

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(['timetable', 'marks', 'results'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium capitalize',
              tab === t ? 'border-brand-500 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t === 'marks' ? 'Marks entry' : t}
          </button>
        ))}
      </div>

      {exam && tab === 'timetable' && <TimetableTab examId={examId} academicYearId={exam.academicYearId} canWrite={canWrite} canDelete={canDelete} />}
      {exam && tab === 'marks' && <MarksTab examId={examId} academicYearId={exam.academicYearId} />}
      {tab === 'results' && <ResultsTab examId={examId} />}
    </>
  );
}

function missingFromError(e: unknown): MissingMarksEntry[] | null {
  const anyE = e as any;
  const code = anyE?.response?.data?.error?.code;
  if (code !== 'MISSING_MARKS') return null;
  return anyE?.response?.data?.error?.details ?? null;
}

// ───── Timetable tab ────────────────────────────────────────────────────────────
function TimetableTab({
  examId,
  academicYearId,
  canWrite,
  canDelete,
}: {
  examId: string;
  academicYearId: string;
  canWrite: boolean;
  canDelete: boolean;
}) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState('');
  const [adding, setAdding] = useState(false);

  const { data: classes = [] } = useQuery({
    queryKey: ['academic-classes', academicYearId],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }).then((r) => r.items),
  });

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['exam-timetable', examId],
    queryFn: () => ExamsApi.listTimetable(examId),
  });

  const remove = useMutation({
    mutationFn: (ttId: string) => ExamsApi.removeTimetableEntry(examId, ttId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exam-timetable', examId] }),
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <Select className="!w-56" value={classId} onChange={(e) => setClassId(e.target.value)}>
          <option value="">Filter by class…</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {canWrite && (
          <button className="btn-primary" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Add subject
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No timetable entries yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Class</Th>
                <Th>Subject</Th>
                <Th>Date</Th>
                <Th>Time</Th>
                <Th>Marks</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries
                .filter((e) => !classId || e.classId === classId)
                .map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-3 text-sm text-slate-700">{e.className}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{e.subjectName}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{e.date?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {e.startTime} – {e.endTime}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {e.totalMarks} (pass {e.passingMarks})
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canDelete && (
                        <button
                          className="text-slate-400 hover:text-red-600"
                          onClick={() => remove.mutate(e.id)}
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddTimetableEntryModal
          examId={examId}
          classes={classes}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            qc.invalidateQueries({ queryKey: ['exam-timetable', examId] });
          }}
        />
      )}
    </div>
  );
}

const ttSchema = z.object({
  classSubjectId: z.string().uuid('Pick a subject'),
  date: z.string().min(1, 'Required'),
  startTime: z.string().min(1, 'Required'),
  endTime: z.string().min(1, 'Required'),
  totalMarks: z.coerce.number().min(1),
  passingMarks: z.coerce.number().min(0),
});
type TtForm = z.infer<typeof ttSchema>;

function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const totalMinutes = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  const hh = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
  const mm = (totalMinutes % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function AddTimetableEntryModal({
  examId,
  classes,
  onClose,
  onAdded,
}: {
  examId: string;
  classes: { id: string; name: string }[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [classId, setClassId] = useState(classes[0]?.id ?? '');
  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = useMemo(() => dedupeBySubject(subjectRows), [subjectRows]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TtForm>({ resolver: zodResolver(ttSchema), defaultValues: { totalMarks: 100, passingMarks: 35 } });

  const [durationMinutes, setDurationMinutes] = useState(60);
  const startTime = watch('startTime');

  useEffect(() => {
    if (startTime && durationMinutes > 0) {
      setValue('endTime', addMinutesToTime(startTime, durationMinutes), { shouldValidate: true });
    }
  }, [startTime, durationMinutes, setValue]);

  const create = useMutation({
    mutationFn: (f: TtForm) => ExamsApi.createTimetableEntry(examId, f),
    onSuccess: onAdded,
  });

  return (
    <Modal open onClose={onClose} title="Add subject to timetable" size="md">
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Class</label>
          <Select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
          <Select {...register('classSubjectId')}>
            <option value="">Select…</option>
            {subjects.map((s) => (
              <option key={s.classSubjectId} value={s.classSubjectId}>
                {s.subjectName}
              </option>
            ))}
          </Select>
          {errors.classSubjectId && <FieldErr msg={errors.classSubjectId.message} />}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Date</label>
          <Input type="date" {...register('date')} />
          {errors.date && <FieldErr msg={errors.date.message} />}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Start time</label>
            <Input type="time" {...register('startTime')} />
            {errors.startTime && <FieldErr msg={errors.startTime.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Duration (mins)</label>
            <Input
              type="number"
              min={1}
              step={5}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">End time</label>
            <Input type="time" readOnly {...register('endTime')} className="bg-slate-50 text-slate-500" />
            {errors.endTime && <FieldErr msg={errors.endTime.message} />}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Total marks</label>
            <Input type="number" {...register('totalMarks')} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Passing marks</label>
            <Input type="number" {...register('passingMarks')} />
          </div>
        </div>
        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending || !classId}>
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ───── Marks entry tab ──────────────────────────────────────────────────────────
function MarksTab({ examId, academicYearId }: { examId: string; academicYearId: string }) {
  const [mode, setMode] = useState<'grid' | 'byStudent'>('grid');
  const [classId, setClassId] = useState('');

  const { data: classes = [] } = useQuery({
    queryKey: ['academic-classes', academicYearId],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }).then((r) => r.items),
  });

  useEffect(() => {
    if (!classId && classes.length) setClassId(classes[0].id);
  }, [classes, classId]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Select className="!w-56" value={classId} onChange={(e) => setClassId(e.target.value)}>
          <option value="">Select class…</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {classLabel(c)}
            </option>
          ))}
        </Select>
        <div className="flex gap-1 rounded-md bg-slate-100 p-1 text-sm">
          <button
            className={cn('rounded px-3 py-1', mode === 'grid' ? 'bg-white shadow-sm' : 'text-slate-500')}
            onClick={() => setMode('grid')}
          >
            By subject (grid)
          </button>
          <button
            className={cn('rounded px-3 py-1', mode === 'byStudent' ? 'bg-white shadow-sm' : 'text-slate-500')}
            onClick={() => setMode('byStudent')}
          >
            By student
          </button>
        </div>
      </div>

      {!classId ? (
        <div className="card p-8 text-center text-slate-500">Select a class to enter marks.</div>
      ) : mode === 'grid' ? (
        <MarksGridEntry examId={examId} classId={classId} />
      ) : (
        <MarksByStudentEntry examId={examId} classId={classId} academicYearId={academicYearId} />
      )}
    </div>
  );
}

function MarksGridEntry({ examId, classId }: { examId: string; classId: string }) {
  const qc = useQueryClient();
  const { data: grid, isLoading } = useQuery({
    queryKey: ['marks-grid', examId, classId],
    queryFn: () => ExamsApi.marksGrid(examId, classId),
  });

  // keyed `${studentId}:${classSubjectId}` → string
  const [cells, setCells] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!grid) return;
    const init: Record<string, string> = {};
    for (const [k, v] of Object.entries(grid.marks)) init[k] = String(v.obtainedMarks);
    setCells(init);
  }, [grid]);

  const [saveError, setSaveError] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: async () => {
      if (!grid) throw new Error('not loaded');
      setSaveError(undefined);
      let saved = 0;
      for (const sub of grid.subjects) {
        const marksData: { studentId: string; obtainedMarks: number }[] = [];
        for (const st of grid.students) {
          const key = `${st.id}:${sub.classSubjectId}`;
          const raw = cells[key];
          const orig = grid.marks[key];
          const origStr = orig ? String(orig.obtainedMarks) : '';
          if (raw === undefined || raw === '' || raw === origStr) continue;
          marksData.push({ studentId: st.id, obtainedMarks: Number(raw) });
        }
        if (!marksData.length) continue;
        const res = await ExamsApi.saveMarksBySubject(examId, sub.classSubjectId, marksData);
        saved += res.saved;
      }
      return { saved };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marks-grid', examId, classId] }),
    onError: (e) => setSaveError(errMsg(e)),
  });

  const setCell = (key: string, val: string) => setCells((p) => ({ ...p, [key]: val }));

  const dirty = useMemo(() => {
    if (!grid) return false;
    for (const [k, v] of Object.entries(cells)) {
      const orig = grid.marks[k];
      const origStr = orig ? String(orig.obtainedMarks) : '';
      if ((v ?? '') !== origStr) return true;
    }
    return false;
  }, [cells, grid]);

  return (
    <>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          <Save className="mr-1.5 h-4 w-4" />
          {save.isPending ? 'Saving…' : 'Save marks'}
        </button>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !grid || grid.students.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No students enrolled in this class for the exam's academic year.</div>
      ) : grid.subjects.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No timetable entries for this class yet — add subjects in the Timetable tab first.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Student
                </th>
                {grid.subjects.map((s) => (
                  <th
                    key={s.classSubjectId}
                    className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {s.subjectName}
                    <div className="font-normal normal-case text-slate-400">/{s.totalMarks}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grid.students.map((st) => (
                <tr key={st.id} className="hover:bg-slate-50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-2">
                    <div className="text-sm font-medium text-slate-900">{st.studentName}</div>
                    <div className="text-xs text-slate-400">
                      {st.rollNumber ? `Roll ${st.rollNumber} · ` : ''}
                      {st.admissionNumber}
                    </div>
                  </td>
                  {grid.subjects.map((sub) => {
                    const key = `${st.id}:${sub.classSubjectId}`;
                    const val = cells[key] ?? '';
                    const num = Number(val);
                    const over = val !== '' && (isNaN(num) || num > sub.totalMarks || num < 0);
                    return (
                      <td key={sub.classSubjectId} className="px-2 py-1.5 text-center">
                        <input
                          value={val}
                          onChange={(e) => setCell(key, e.target.value)}
                          placeholder="—"
                          className={cn(
                            'w-16 rounded-md border px-2 py-1 text-center text-sm focus:outline-none focus:ring-1',
                            over
                              ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-400'
                              : 'border-slate-200 focus:border-brand-400 focus:ring-brand-400',
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {save.isSuccess && !dirty && (
        <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Saved {save.data?.saved} marks.</div>
      )}
      {saveError && <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</div>}
    </>
  );
}

function MarksByStudentEntry({ examId, classId, academicYearId }: { examId: string; classId: string; academicYearId: string }) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState('');

  const { data: studentsPage } = useQuery({
    queryKey: ['students', classId, academicYearId],
    queryFn: () => StudentsApi.list({ classId, academicYearId, limit: 100 }),
    enabled: !!classId,
  });
  const students = studentsPage?.items ?? [];

  const { data: timetable = [] } = useQuery({
    queryKey: ['exam-timetable', examId],
    queryFn: () => ExamsApi.listTimetable(examId),
  });
  const entries = timetable.filter((t: ExamTimetableEntry) => t.classId === classId);

  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => setValues({}), [studentId]);

  const [saveError, setSaveError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: () => {
      const marksData = entries
        .filter((e) => values[e.classSubjectId] !== undefined && values[e.classSubjectId] !== '')
        .map((e) => ({ classSubjectId: e.classSubjectId, obtainedMarks: Number(values[e.classSubjectId]) }));
      if (!marksData.length) throw new Error('Enter at least one mark');
      return ExamsApi.saveMarksByStudent(examId, studentId, marksData);
    },
    onSuccess: () => {
      setSaveError(undefined);
      qc.invalidateQueries({ queryKey: ['marks-grid', examId] });
    },
    onError: (e) => setSaveError(errMsg(e)),
  });

  return (
    <div className="card p-4">
      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-slate-700">Student</label>
        <Select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
          <option value="">Select…</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name ?? '(no linked user)'} {s.rollNumber ? `— Roll ${s.rollNumber}` : ''}
            </option>
          ))}
        </Select>
      </div>

      {studentId && (
        <>
          {entries.length === 0 ? (
            <div className="py-4 text-center text-sm text-slate-500">No timetable entries for this class yet.</div>
          ) : (
            <div className="space-y-2">
              {entries.map((e) => (
                <div key={e.classSubjectId} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <span className="text-sm text-slate-700">
                    {e.subjectName} <span className="text-slate-400">/{e.totalMarks}</span>
                  </span>
                  <input
                    value={values[e.classSubjectId] ?? ''}
                    onChange={(ev) => setValues((p) => ({ ...p, [e.classSubjectId]: ev.target.value }))}
                    placeholder="—"
                    className="w-20 rounded-md border border-slate-200 px-2 py-1 text-center text-sm focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
                  <Save className="mr-1.5 h-4 w-4" />
                  {save.isPending ? 'Saving…' : 'Save marks'}
                </button>
              </div>
              {save.isSuccess && <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Saved.</div>}
              {saveError && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ───── Results tab ──────────────────────────────────────────────────────────────
function ResultsTab({ examId }: { examId: string }) {
  const { data: results = [], isLoading } = useQuery({
    queryKey: ['exam-results', examId],
    queryFn: () => ExamsApi.results(examId),
  });

  if (isLoading) return <div className="card p-8 text-center text-slate-400">Loading…</div>;
  if (results.length === 0) return <div className="card p-8 text-center text-slate-500">No published results yet — publish the exam to see results here.</div>;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <Th>Student</Th>
            <Th>Marks</Th>
            <Th>Percentage</Th>
            <Th>Grade</Th>
            <Th>Result</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {results.map((r) => (
            <tr key={r.studentId}>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.studentName}</td>
              <td className="px-4 py-3 text-sm text-slate-600">
                {r.totalObtained} / {r.totalMax}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600">{r.overallPercent}%</td>
              <td className="px-4 py-3 text-sm text-slate-600">{r.cumulativeGrade}</td>
              <td className="px-4 py-3">
                <Badge tone={r.finalPassStatus ? 'green' : 'red'}>{r.finalPassStatus ? 'Pass' : 'Fail'}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───── helpers ────────────────────────────────────────────────────────────────
function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>
  );
}

function FieldErr({ msg }: { msg?: string }) {
  return <p className="mt-1 text-xs text-red-600">{msg}</p>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
